import { useEffect, useState } from "react";

import { adminUiCopy } from "@/copy/adminUiCopy";
import {
  fetchEnvironmentSafety,
  type StripeSafetyInfo,
} from "@/services/health/EnvironmentSafetyService";
import { VNextNotice } from "@/vnext/primitives";

export interface RevenueStripeSafetyState {
  isLoading: boolean;
  stripeSafety: StripeSafetyInfo | null;
}

export interface ResolvedRevenueStripeSafetyState {
  allowStripeMutations: boolean;
  message: string;
  tone: "info" | "warning" | "danger";
}

export const resolveRevenueStripeSafety = (
  state: RevenueStripeSafetyState,
): ResolvedRevenueStripeSafetyState => {
  if (state.isLoading) {
    return {
      allowStripeMutations: false,
      message: adminUiCopy.revenueSafety.checking,
      tone: "warning",
    };
  }

  if (!state.stripeSafety || state.stripeSafety.runtimeMode === "unknown") {
    return {
      allowStripeMutations: false,
      message: adminUiCopy.revenueSafety.unavailable,
      tone: "warning",
    };
  }

  if (state.stripeSafety.modeMatchesConfiguration === false) {
    return {
      allowStripeMutations: false,
      message: adminUiCopy.revenueSafety.mismatch,
      tone: "danger",
    };
  }

  if (
    state.stripeSafety.runtimeMode === "live" ||
    state.stripeSafety.configuredMode === "live"
  ) {
    return {
      allowStripeMutations: false,
      message: adminUiCopy.revenueSafety.liveMode,
      tone: "danger",
    };
  }

  return {
    allowStripeMutations: true,
    message: adminUiCopy.revenueSafety.safe,
    tone: "info",
  };
};

export const useRevenueStripeSafety = (): RevenueStripeSafetyState => {
  const [state, setState] = useState<RevenueStripeSafetyState>({
    isLoading: true,
    stripeSafety: null,
  });

  useEffect(() => {
    let cancelled = false;

    void fetchEnvironmentSafety().then((result) => {
      if (cancelled) {
        return;
      }

      setState({
        isLoading: false,
        stripeSafety: result?.stripeSafety ?? null,
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

export const RevenueStripeSafetyNotice = () => {
  const state = useRevenueStripeSafety();
  const resolved = resolveRevenueStripeSafety(state);

  return <VNextNotice tone={resolved.tone}>{resolved.message}</VNextNotice>;
};
